import { Box, Flex, Input, Stack, Text } from "@chakra-ui/react";
import { useState } from "react";
import {
  FiBriefcase,
  FiCalendar,
  FiCreditCard,
  FiDollarSign,
  FiHash,
  FiPercent,
  FiSearch,
  FiShoppingBag,
  FiUser
} from "react-icons/fi";
import { DropdownField } from "components/dropdown-field";
import {
  dropdownSimNao,
  dropdownTipoPagamento
} from "components/atendimentos-components/atendimento-form/fields-data";
import { StateFilterValue } from "components/pipeline-components/classes/state-machine/filters";
import { StatesMachine } from "components/pipeline-components/classes/state-machine/state-machine";
import { dropdownBancos } from "components/usuarios-bancos-components/consts";
import { FilterCard, FilterGrid } from "../components/FilterCard";
import { MinMaxInput } from "../components/MinMaxInput";
import { SidePanelAccordion } from "../components/SidePanelAccordion";
import { DiagnosticPanel } from "./DiagnosticPanel";

const ALL_FILTER_LABELS: Record<string, string> = {
  idade: "Idade",
  banco_operacao: "Banco da operação",
  saldo_devedor: "Saldo devedor",
  taxa_contrato: "Taxa do contrato",
  valor_parcela: "Valor da parcela",
  valor_liberado: "Valor líquido do cliente",
  quantidade_contratos: "Quantidade de contratos",
  numero_parcelas_pagas: "Número de parcelas pagas",
  margem_aumento: "Margem de aumento",
  especies: "Espécie",
  possui_representante_legal: "Representante legal",
  meio_pagamento: "Meio de pagamento",
  valor_beneficio: "Valor do benefício",
  ddb: "DDB"
};

export function FiltroEditor({
  isDisabled,
  pipelineData,
  updatePipelineData,
  bancoOperacaoOptions,
  selectedStateId,
  diagnosticUpdatePipelineData
}: {
  isDisabled: boolean;
  pipelineData?: StatesMachine;
  updatePipelineData: (
    updater: (draft: StatesMachine) => StatesMachine | void
  ) => void;
  bancoOperacaoOptions: { name?: string; value: string | number }[];
  selectedStateId?: number;
  diagnosticUpdatePipelineData?: (
    updater: (draft: StatesMachine) => StatesMachine | void
  ) => void;
}) {
  const [search, setSearch] = useState("");

  const getFilterValue = (key: string) => pipelineData?.getFilterValue(key);
  const getFilterRangeValue = (key: string) =>
    pipelineData?.getFilterRangeValue(key);
  const getFilterMultiValue = (key: string) =>
    pipelineData?.getFilterMultiValue(key) ?? [];

  const setRange = (key: string, bound: "gte" | "lte", value: string) =>
    updatePipelineData((draft) => {
      draft.setFilterRangeValue(key, bound, value);
    });

  const setRangeDate = (key: string, bound: "gte" | "lte", value: string) =>
    updatePipelineData((draft) => {
      draft.setFilterRangeValue(key, bound, value, true);
    });

  const setSimple = (key: string, value: StateFilterValue) =>
    updatePipelineData((draft) => {
      draft.setFilterSimpleValue(key, value);
    });

  const setMulti = (key: string, values: (string | number)[]) =>
    updatePipelineData((draft) => {
      draft.setFilterMultiValue(key, values);
    });

  const searchLower = search.trim().toLowerCase();
  const isVisible = (key: string) =>
    !searchLower || ALL_FILTER_LABELS[key]?.toLowerCase().includes(searchLower);

  const perfil_keys = [
    "idade",
    "ddb",
    "especies",
    "possui_representante_legal",
    "valor_beneficio"
  ];
  const contratos_keys = [
    "banco_operacao",
    "quantidade_contratos",
    "numero_parcelas_pagas"
  ];
  const valores_keys = [
    "saldo_devedor",
    "taxa_contrato",
    "valor_parcela",
    "valor_liberado",
    "margem_aumento"
  ];
  const pagamento_keys = ["meio_pagamento"];

  const groupHasVisible = (keys: string[]) =>
    !searchLower || keys.some((k) => isVisible(k));

  return (
    <Stack spacing="12px">
      {/* Search */}
      <Flex
        align="center"
        gap="8px"
        bg="white"
        border="1px solid"
        borderColor="#E2E8F0"
        borderRadius="10px"
        px="12px"
        py="9px"
        boxShadow="0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)"
      >
        <Box color="#90a1b9" flexShrink={0}>
          <FiSearch size={15} />
        </Box>
        <Input
          variant="unstyled"
          border="none !important"
          bg="transparent"
          placeholder="Buscar filtro"
          fontSize="13px"
          color="#334155"
          _placeholder={{ color: "#CBD5E1" }}
          _hover={{ bg: "transparent !important" }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Flex>

      {/* Perfil do beneficiário */}
      {groupHasVisible(perfil_keys) && (
        <SidePanelAccordion
          icon={FiUser}
          iconBgColor="#EDE9FE"
          iconColor="#7C3AED"
          title="Perfil do beneficiário"
          subtitle="Dados pessoais e características do beneficiário"
          activeCount={0}
        >
          <FilterGrid>
            {isVisible("idade") && (
              <FilterCard
                label="Idade"
                typeIcon={FiHash}
                isDisabled={isDisabled}
              >
                <MinMaxInput
                  isDisabled={isDisabled}
                  minValue={getFilterRangeValue("idade")?.gte ?? ""}
                  maxValue={getFilterRangeValue("idade")?.lte ?? ""}
                  onChangeMin={(v) => setRange("idade", "gte", v)}
                  onChangeMax={(v) => setRange("idade", "lte", v)}
                />
              </FilterCard>
            )}

            {isVisible("ddb") && (
              <FilterCard
                label="DDB"
                typeIcon={FiCalendar}
                isDisabled={isDisabled}
              >
                <MinMaxInput
                  isDisabled={isDisabled}
                  inputType="date"
                  minValue={getFilterRangeValue("ddb")?.gte ?? ""}
                  maxValue={getFilterRangeValue("ddb")?.lte ?? ""}
                  onChangeMin={(v) => setRangeDate("ddb", "gte", v)}
                  onChangeMax={(v) => setRangeDate("ddb", "lte", v)}
                />
              </FilterCard>
            )}

            {isVisible("especies") && (
              <FilterCard label="Espécie" isDisabled={isDisabled} fullWidth>
                <DropdownField
                  isDisabled={isDisabled}
                  options={bancoOperacaoOptions}
                  dropdownProps={{
                    w: "100%",
                    variant: "filterInput",
                    multiSelect: true,
                    searchOptions: true,
                    children:
                      getFilterMultiValue("especies")?.map(
                        (value) =>
                          bancoOperacaoOptions.find(
                            (option) => option.value === value
                          )?.name || value
                      ) || undefined
                  }}
                  value={getFilterMultiValue("especies")}
                  onChange={(values) => setMulti("especies", values)}
                />
              </FilterCard>
            )}

            {isVisible("possui_representante_legal") && (
              <FilterCard label="Representante legal" isDisabled={isDisabled}>
                <DropdownField
                  isDisabled={isDisabled}
                  options={dropdownSimNao}
                  dropdownProps={{ w: "100%", variant: "filterInput" }}
                  value={getFilterValue("possui_representante_legal")}
                  onChange={(v) => setSimple("possui_representante_legal", v)}
                />
              </FilterCard>
            )}

            {isVisible("valor_beneficio") && (
              <FilterCard
                label="Valor do benefício"
                typeIcon={FiDollarSign}
                isDisabled={isDisabled}
              >
                <MinMaxInput
                  isDisabled={isDisabled}
                  minValue={getFilterRangeValue("valor_beneficio")?.gte ?? ""}
                  maxValue={getFilterRangeValue("valor_beneficio")?.lte ?? ""}
                  onChangeMin={(v) => setRange("valor_beneficio", "gte", v)}
                  onChangeMax={(v) => setRange("valor_beneficio", "lte", v)}
                />
              </FilterCard>
            )}
          </FilterGrid>
        </SidePanelAccordion>
      )}

      {/* Contratos e operação */}
      {groupHasVisible(contratos_keys) && (
        <SidePanelAccordion
          icon={FiBriefcase}
          iconBgColor="#DBEAFE"
          iconColor="#2563EB"
          title="Contratos e operação"
          subtitle="Banco, quantidade e estágio dos contratos"
          activeCount={0}
        >
          <FilterGrid>
            {isVisible("banco_operacao") && (
              <FilterCard
                label="Banco da operação"
                isDisabled={isDisabled}
                fullWidth
              >
                <DropdownField
                  isDisabled={isDisabled}
                  options={dropdownBancos}
                  dropdownProps={{
                    w: "100%",
                    variant: "filterInput",
                    multiSelect: true
                  }}
                  value={getFilterMultiValue("banco_operacao")}
                  onChange={(v) => setMulti("banco_operacao", v)}
                />
              </FilterCard>
            )}

            {isVisible("quantidade_contratos") && (
              <FilterCard
                label="Quantidade de contratos"
                typeIcon={FiHash}
                isDisabled={isDisabled}
              >
                <MinMaxInput
                  isDisabled={isDisabled}
                  minValue={
                    getFilterRangeValue("quantidade_contratos")?.gte ?? ""
                  }
                  maxValue={
                    getFilterRangeValue("quantidade_contratos")?.lte ?? ""
                  }
                  onChangeMin={(v) =>
                    setRange("quantidade_contratos", "gte", v)
                  }
                  onChangeMax={(v) =>
                    setRange("quantidade_contratos", "lte", v)
                  }
                />
              </FilterCard>
            )}

            {isVisible("numero_parcelas_pagas") && (
              <FilterCard
                label="Número de parcelas pagas"
                typeIcon={FiHash}
                isDisabled={isDisabled}
              >
                <MinMaxInput
                  isDisabled={isDisabled}
                  minValue={
                    getFilterRangeValue("numero_parcelas_pagas")?.gte ?? ""
                  }
                  maxValue={
                    getFilterRangeValue("numero_parcelas_pagas")?.lte ?? ""
                  }
                  onChangeMin={(v) =>
                    setRange("numero_parcelas_pagas", "gte", v)
                  }
                  onChangeMax={(v) =>
                    setRange("numero_parcelas_pagas", "lte", v)
                  }
                />
              </FilterCard>
            )}
          </FilterGrid>
        </SidePanelAccordion>
      )}

      {/* Valores e margem */}
      {groupHasVisible(valores_keys) && (
        <SidePanelAccordion
          icon={FiShoppingBag}
          iconBgColor="#D1FAE5"
          iconColor="#059669"
          title="Valores e margem"
          subtitle="Faixas financeiras para priorizar oportunidades"
          activeCount={0}
        >
          <FilterGrid>
            {isVisible("saldo_devedor") && (
              <FilterCard
                label="Saldo devedor"
                typeIcon={FiDollarSign}
                isDisabled={isDisabled}
              >
                <MinMaxInput
                  isDisabled={isDisabled}
                  minValue={getFilterRangeValue("saldo_devedor")?.gte ?? ""}
                  maxValue={getFilterRangeValue("saldo_devedor")?.lte ?? ""}
                  onChangeMin={(v) => setRange("saldo_devedor", "gte", v)}
                  onChangeMax={(v) => setRange("saldo_devedor", "lte", v)}
                />
              </FilterCard>
            )}

            {isVisible("taxa_contrato") && (
              <FilterCard
                label="Taxa do contrato"
                typeIcon={FiPercent}
                isDisabled={isDisabled}
              >
                <MinMaxInput
                  isDisabled={isDisabled}
                  minValue={getFilterRangeValue("taxa_contrato")?.gte ?? ""}
                  maxValue={getFilterRangeValue("taxa_contrato")?.lte ?? ""}
                  onChangeMin={(v) => setRange("taxa_contrato", "gte", v)}
                  onChangeMax={(v) => setRange("taxa_contrato", "lte", v)}
                />
              </FilterCard>
            )}

            {isVisible("valor_parcela") && (
              <FilterCard
                label="Valor da parcela"
                typeIcon={FiDollarSign}
                isDisabled={isDisabled}
              >
                <MinMaxInput
                  isDisabled={isDisabled}
                  minValue={getFilterRangeValue("valor_parcela")?.gte ?? ""}
                  maxValue={getFilterRangeValue("valor_parcela")?.lte ?? ""}
                  onChangeMin={(v) => setRange("valor_parcela", "gte", v)}
                  onChangeMax={(v) => setRange("valor_parcela", "lte", v)}
                />
              </FilterCard>
            )}

            {isVisible("valor_liberado") && (
              <FilterCard
                label="Valor líquido do cliente"
                typeIcon={FiDollarSign}
                isDisabled={isDisabled}
              >
                <MinMaxInput
                  isDisabled={isDisabled}
                  minValue={getFilterRangeValue("valor_liberado")?.gte ?? ""}
                  maxValue={getFilterRangeValue("valor_liberado")?.lte ?? ""}
                  onChangeMin={(v) => setRange("valor_liberado", "gte", v)}
                  onChangeMax={(v) => setRange("valor_liberado", "lte", v)}
                />
              </FilterCard>
            )}

            {isVisible("margem_aumento") && (
              <FilterCard
                label="Margem de aumento"
                typeIcon={FiPercent}
                isDisabled={isDisabled}
              >
                <MinMaxInput
                  isDisabled={isDisabled}
                  minValue={getFilterRangeValue("margem_aumento")?.gte ?? ""}
                  maxValue={getFilterRangeValue("margem_aumento")?.lte ?? ""}
                  onChangeMin={(v) => setRange("margem_aumento", "gte", v)}
                  onChangeMax={(v) => setRange("margem_aumento", "lte", v)}
                />
              </FilterCard>
            )}
          </FilterGrid>
        </SidePanelAccordion>
      )}

      {/* Pagamento e recebimento */}
      {groupHasVisible(pagamento_keys) && (
        <SidePanelAccordion
          icon={FiCreditCard}
          iconBgColor="#FEF3C7"
          iconColor="#D97706"
          title="Pagamento e recebimento"
          subtitle="Forma de crédito e dados ligados ao pagamento"
          activeCount={0}
        >
          <FilterGrid>
            {isVisible("meio_pagamento") && (
              <FilterCard
                label="Meio de pagamento"
                typeIcon={FiCreditCard}
                isDisabled={isDisabled}
                fullWidth
              >
                <DropdownField
                  isDisabled={isDisabled}
                  options={dropdownTipoPagamento}
                  dropdownProps={{ w: "100%", variant: "filterInput" }}
                  value={getFilterValue("meio_pagamento")}
                  onChange={(v) => setSimple("meio_pagamento", v)}
                />
              </FilterCard>
            )}
          </FilterGrid>
        </SidePanelAccordion>
      )}

      {searchLower &&
        !groupHasVisible(perfil_keys) &&
        !groupHasVisible(contratos_keys) &&
        !groupHasVisible(valores_keys) &&
        !groupHasVisible(pagamento_keys) && (
          <Text fontSize="13px" color="#90a1b9" textAlign="center" py="12px">
            Nenhum filtro encontrado para &quot;{search}&quot;
          </Text>
        )}
      {selectedStateId != null &&
        pipelineData &&
        diagnosticUpdatePipelineData && (
          <DiagnosticPanel
            selectedStateId={selectedStateId}
            pipelineData={pipelineData}
            operation="FILTRO"
            updatePipelineData={diagnosticUpdatePipelineData}
          />
        )}
    </Stack>
  );
}
